import { useProductInfo } from "@expert/contentful";
import { Flex } from "@mantine/core";
import { type JSX, useEffect } from "react";
import { useAnalytics } from "@/analytics";
import { usePartner } from "@/sdk";
import { useSessionId } from "@/sdk/sessions/hooks/useSessionId";
import { userCache } from "@/shared-utils";
import { useProductOfferStore } from "../stores";
import { EligibilityStatus, SaleProgress } from "../types";
import classes from "./HomeProductFlow.module.css";
import { IntegratedFlow } from "./IntegratedFlow";
import { EnrollFailureScreen } from "./StepSpecific/EnrollFailure";
import { SaleView } from "./StepSpecific/SaleView";

export function HomeProductFlow({ onPrimarySaleComplete }: { onPrimarySaleComplete?: () => void }): JSX.Element {
    const { dispatcher } = useAnalytics();
    const sessionId = useSessionId();
    const primaryProducts = useProductOfferStore((state) => state.getPrimaryProducts(sessionId));
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));
    const partner = usePartner();

    useEffect(() => {
        void dispatcher.dispatchAction("PageShown", "sale-tool");
        // oxlint-disable-next-line exhaustive-deps
    }, []);

    // when everything is moved to the unified sales view, we can just use selectedProduct
    // this is a fallback that handles non-unified views which only have 1 primary product
    const guidingProduct =
        selectedProduct && selectedProduct.isPrimaryProduct ? selectedProduct : primaryProducts.at(0);

    const { productInfo } = useProductInfo(
        partner,
        userCache.employeeId,
        guidingProduct?.productSku ? [guidingProduct?.productSku] : undefined,
    );

    if (primaryProducts.length === 0) {
        return <SaleView status={EligibilityStatus.Unknown} />;
    }

    return guidingProduct?.saleProgress === SaleProgress.InProgress &&
        guidingProduct.status === EligibilityStatus.Eligible ? (
        <Flex className={classes.flexContainer}>
            <IntegratedFlow product={guidingProduct} />
        </Flex>
    ) : (
        <Flex className={classes.flexContainer} data-testid="home-product-flow-container">
            {/* Customer Not eligible */}
            {guidingProduct &&
            guidingProduct.status !== EligibilityStatus.Eligible &&
            guidingProduct.saleProgress === SaleProgress.InProgress ? (
                <SaleView onPrimarySaleComplete={onPrimarySaleComplete} status={guidingProduct.status} />
            ) : null}

            {/* Endgame */}
            {guidingProduct?.saleProgress === SaleProgress.Success ||
            guidingProduct?.saleProgress === SaleProgress.Declined ? (
                <SaleView
                    onPrimarySaleComplete={onPrimarySaleComplete}
                    status={guidingProduct.saleProgress}
                    productInfo={productInfo?.[guidingProduct.productSku]}
                />
            ) : null}

            {/* Alternate Endgame */}
            {guidingProduct?.saleProgress === SaleProgress.Failure ? (
                <EnrollFailureScreen isProductAsurionBilled={guidingProduct.isProductAsurionBilled} />
            ) : null}
        </Flex>
    );
}
